/**
 * App-level feedback primitives: toasts + a promise-based confirm dialog.
 *
 * Replaces native alert()/confirm()/prompt() (blocked by the no-alert lint
 * rule and inconsistent with the app's styling).
 *
 * Usage:
 *   const { toast, confirm } = useFeedback()
 *   toast('Saved!', { variant: 'success' })
 *   const ok = await confirm({ title: 'Delete?', destructive: true })
 *   const reason = await confirm({ title: 'Reject', input: { label: 'Reason' } }) // string | null
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

type ToastVariant = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  message: string
  variant: ToastVariant
}

interface ConfirmInput {
  label: string
  placeholder?: string
}

export interface ConfirmOptions {
  title: string
  message?: string
  confirmLabel?: string
  cancelLabel?: string
  /** Renders the confirm button in the error/red style */
  destructive?: boolean
  /** Optional text input (prompt-style). When set, confirm resolves the entered string, or null on cancel. */
  input?: ConfirmInput
}

interface ConfirmFn {
  (options: ConfirmOptions & { input: ConfirmInput }): Promise<string | null>
  (options: ConfirmOptions): Promise<boolean>
}

interface FeedbackContextValue {
  toast: (message: string, options?: { variant?: ToastVariant; duration?: number }) => void
  confirm: ConfirmFn
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null)

// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its provider; dev-only Fast Refresh hint, no runtime impact
export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext)
  if (!ctx) throw new Error('useFeedback must be used within <FeedbackProvider>')
  return ctx
}

interface PendingConfirm {
  options: ConfirmOptions
  resolve: (value: boolean | string | null) => void
}

const TOAST_STYLES: Record<ToastVariant, string> = {
  success: 'border-success/40 bg-success text-success-foreground',
  error: 'border-error/40 bg-error text-error-foreground',
  info: 'border-border bg-surface text-foreground',
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const [inputValue, setInputValue] = useState('')
  const nextId = useRef(0)

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (message: string, options?: { variant?: ToastVariant; duration?: number }) => {
      const id = nextId.current++
      setToasts((prev) => [...prev, { id, message, variant: options?.variant ?? 'info' }])
      window.setTimeout(() => dismissToast(id), options?.duration ?? 4000)
    },
    [dismissToast]
  )

  const confirm = useCallback((options: ConfirmOptions) => {
    setInputValue('')
    return new Promise<boolean | string | null>((resolve) => {
      setPending({ options, resolve })
    })
  }, []) as ConfirmFn

  const settle = useCallback(
    (confirmed: boolean) => {
      if (!pending) return
      if (pending.options.input) {
        pending.resolve(confirmed ? inputValue : null)
      } else {
        pending.resolve(confirmed)
      }
      setPending(null)
    },
    [pending, inputValue]
  )

  // Close the confirm dialog on Escape (document level, so it works regardless of focus)
  useEffect(() => {
    if (!pending) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') settle(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [pending, settle])

  const options = pending?.options

  return (
    <FeedbackContext.Provider value={{ toast, confirm }}>
      {children}

      {/* Confirm dialog */}
      {options && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- backdrop dismiss; Escape handled at document level
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => settle(false)}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- stops backdrop-dismiss clicks from bubbling; not an interactive control */}
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="feedback-confirm-title"
            aria-describedby={options.message ? 'feedback-confirm-message' : undefined}
            className="border-border bg-surface w-full max-w-md rounded-xl border p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="feedback-confirm-title" className="text-foreground text-lg font-semibold">
              {options.title}
            </h2>
            {options.message && (
              <p id="feedback-confirm-message" className="text-foreground-muted mt-2 text-sm">
                {options.message}
              </p>
            )}
            {options.input && (
              <label className="mt-4 block">
                <span className="text-foreground mb-1 block text-sm font-medium">{options.input.label}</span>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={options.input.placeholder}
                  className="border-border bg-surface text-foreground placeholder:text-foreground-muted focus:ring-accent w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
                />
              </label>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => settle(false)}
                className="border-border bg-surface text-foreground-muted hover:bg-surface-hover hover:text-foreground rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
              >
                {options.cancelLabel ?? 'Cancel'}
              </button>
              <button
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: move focus into the dialog when it opens
                autoFocus
                type="button"
                onClick={() => settle(true)}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  options.destructive
                    ? 'bg-error text-error-foreground hover:bg-error/90'
                    : 'bg-foreground text-background hover:bg-foreground/90'
                }`}
              >
                {options.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toasts */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 z-[60] flex w-full max-w-sm flex-col gap-2"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg ${TOAST_STYLES[t.variant]}`}
          >
            <span>{t.message}</span>
            <button
              type="button"
              onClick={() => dismissToast(t.id)}
              aria-label="Dismiss notification"
              className="shrink-0 font-bold opacity-70 transition-opacity hover:opacity-100"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </FeedbackContext.Provider>
  )
}
